import type { UiSpendSeries, UiSpendSeriesCell } from '@nightshift/ui-types';

import {
  CHART_OTHER_COLOUR,
  MAX_CHART_SERIES,
  assignSeriesColours,
} from './chart-palette.ts';

/**
 * Arithmetic behind the Ops production chart: a day on x, spend on y, one stack
 * segment per machine or per project.
 *
 * Kept out of the component so it is testable in node — vitest collects `.ts`,
 * never `.tsx`.
 */

export type SpendDimension = 'machine' | 'project';
export type SpendMeasure = 'usd' | 'tokens';

export const SPEND_OTHER_KEY = 'Other';
export const UNATTRIBUTED_KEY = 'unattributed';
/** Colour-coded series before the tail folds into `Other`. */
export const MAX_SPEND_SERIES = MAX_CHART_SERIES;

interface SpendSeriesKey {
  key: string;
  label: string;
  colour: string;
  total: number;
  /**
   * False when every contributing row was unpriced. `total` is then 0 because
   * nothing could be added to it, which is "unknown", not "free" — the legend has
   * to say so rather than printing $0.00.
   */
  priced: boolean;
}

export interface SpendDay {
  day: string;
  /** Segment value per series key. Missing keys are absent, not zero. */
  values: Record<string, number>;
  total: number;
  entries: number;
  unpriced: number;
}

export interface SpendPlot {
  days: SpendDay[];
  keys: SpendSeriesKey[];
  max: number;
  measure: SpendMeasure;
  dimension: SpendDimension;
  /** True when no cell in the window carried a value for this measure. */
  empty: boolean;
}

function cellValue(cell: UiSpendSeriesCell, measure: SpendMeasure): number {
  if (measure === 'tokens') return cell.tokens;
  return cell.usd ?? 0;
}

function cellKey(cell: UiSpendSeriesCell, dimension: SpendDimension): string {
  if (dimension === 'project') return cell.project;
  return cell.machine ?? UNATTRIBUTED_KEY;
}

/**
 * Fold the sparse cells into one stack per day.
 *
 * Series are ranked by window total so the biggest spender takes slot one and the
 * tail folds into `Other`, then the surviving keys are sorted by name so a stack's
 * order does not shuffle as the numbers move underneath it.
 */
export function buildSpendPlot(
  series: UiSpendSeries,
  dimension: SpendDimension,
  measure: SpendMeasure,
): SpendPlot {
  const totals = new Map<string, number>();
  const priced = new Set<string>();
  for (const cell of series.cells) {
    const key = cellKey(cell, dimension);
    totals.set(key, (totals.get(key) ?? 0) + cellValue(cell, measure));
    // Tokens are always reported; only dollars can be structurally absent.
    if (measure === 'tokens' || cell.usd !== null) priced.add(key);
  }

  const ranked = [...totals.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key]) => key);
  const named = ranked.slice(0, MAX_SPEND_SERIES);
  const folded = new Set(ranked.slice(MAX_SPEND_SERIES));

  const ordered = [...named].sort((a, b) => a.localeCompare(b));
  const colours = assignSeriesColours(named);

  const byDay = new Map<string, SpendDay>();
  for (const day of series.days) {
    byDay.set(day, { day, values: {}, total: 0, entries: 0, unpriced: 0 });
  }

  for (const cell of series.cells) {
    const bucket = byDay.get(cell.day);
    if (!bucket) continue;
    const raw = cellKey(cell, dimension);
    const key = folded.has(raw) ? SPEND_OTHER_KEY : raw;
    const value = cellValue(cell, measure);
    if (value > 0) {
      bucket.values[key] = (bucket.values[key] ?? 0) + value;
      bucket.total += value;
    }
    bucket.entries += cell.entries;
    bucket.unpriced += cell.unpriced;
  }

  const days = series.days.map((day) => byDay.get(day)!);
  const keys: SpendSeriesKey[] = ordered.map((key) => ({
    key,
    label: key === UNATTRIBUTED_KEY ? 'unattributed' : key,
    colour: colours.get(key)!,
    total: totals.get(key) ?? 0,
    priced: priced.has(key),
  }));
  if (folded.size > 0) {
    keys.push({
      key: SPEND_OTHER_KEY,
      label: `Other (${folded.size})`,
      colour: CHART_OTHER_COLOUR,
      total: [...folded].reduce((sum, key) => sum + (totals.get(key) ?? 0), 0),
      priced: [...folded].some((key) => priced.has(key)),
    });
  }

  const max = days.reduce((high, day) => Math.max(high, day.total), 0);
  return {
    days,
    keys,
    max,
    measure,
    dimension,
    empty: max === 0,
  };
}

/** Stack order is the legend order, so a segment keeps its band across the window. */
export function stackKeys(plot: SpendPlot): string[] {
  return plot.keys.map((entry) => entry.key);
}

/**
 * The note under the chart.
 *
 * Says "unknown" rather than drawing $0 when a window is entirely unpriced, and
 * always owns the gap between this chart and the attempts-only spend tiles.
 */
export function spendChartNote(series: UiSpendSeries, plot: SpendPlot): string {
  const base = 'Includes sidecar spend; the spend tiles count attempts only.';
  if (plot.measure === 'tokens') return base;
  if (series.unpricedEntries === 0) return base;
  const entries = series.unpricedEntries;
  const label = entries === 1 ? '1 entry has' : `${entries} entries have`;
  return `${label} no dollar price (Cursor, Kimi) and are absent from these bars — switch to tokens to include them. ${base}`;
}
